import * as Sentry from '@sentry/svelte';

import { userStore } from '@client/stores/user';

const DSN = import.meta.env.VITE_SENTRY_DSN;

// 'production' | 'preview' | 'development', derived from VERCEL_ENV at build time.
// Not vite's PROD flag: that is true for preview builds too, which would file preview
// errors under production — with minified traces, since previews upload no sourcemaps.
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT;

// Text is masked by default; these are the only elements allowed through. Every
// selector here was checked against real markup — chrome and app-authored copy only,
// never customer employee data. Add `.sentry-unmask` to opt an element in.
const UNMASK_SELECTORS = [
  '.sentry-unmask',
  '[data-sentry-unmask]',
  '.page-title', // page/page-header.svelte — route title
  '.notification', // notifications/notification.svelte — toast the user saw
  '.banners', // banners/banners.svelte — app-level banner copy
  '.auth-error', // app/app.svelte — login failure text
  '.version' // sidebar/sidebar.svelte — build version
];

// Production reports by default; previews only when VITE_SENTRY_ENABLED is set in
// Vercel's preview scope. Local development never reports — not even opted in.
export const resolveSentryEnabled = ({ dsn, environment, optIn }) =>
  Boolean(dsn) && (environment === 'production' || (environment === 'preview' && optIn === 'true'));

export const isSentryEnabled = () =>
  resolveSentryEnabled({ dsn: DSN, environment: ENVIRONMENT, optIn: import.meta.env.VITE_SENTRY_ENABLED });

// The backend passes the JWT as ?token= on first login. It does not persist in the URL,
// but init runs inside that window, so it can still reach an event or a breadcrumb.
export const scrubUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return url;
  }

  try {
    const parsed = new URL(url, window.location.origin);

    if (!parsed.searchParams.has('token')) {
      return url;
    }

    parsed.searchParams.set('token', '[Filtered]');

    return parsed.toString();
  } catch {
    return url;
  }
};

// Returns a userStore subscriber. Identity has to follow the store rather than a
// component: staff switch domains mid-session via four separate call sites, and each
// one replaces userStore without remounting. A component would identify once and go
// stale.
export const createIdentitySync = () => {
  let identity = '';

  return (user) => {
    const email = user?.currentUser?.email;

    if (!email) {
      return;
    }

    const domain = user?.domain;
    const next = `${email}|${domain}`;

    if (next === identity) {
      return;
    }

    const previousDomain = identity ? identity.split('|')[1] : null;

    identity = next;

    Sentry.setUser({ id: email, email, username: email });
    Sentry.setTag('domain', domain);
    Sentry.setTag('isStaff', Boolean(user.isStaff));
    Sentry.setTag('isManaging', Boolean(user.isManaging));

    if (previousDomain && previousDomain !== domain) {
      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'info',
        message: `Domain switched: ${previousDomain} → ${domain}`
      });
    }
  };
};

// Outlook destroys the add-in pane as users switch messages, aborting the bootstrap
// UserService.get as a statusless network error. Worth an issue, not a replay apiece.
//
// The localStorage recovery notice is a captureMessage, and `isErrorEvent` is `!event.type`,
// so it samples a replay like any error would. A storage wipe leaves no DOM trace to watch.
export const shouldSampleErrorForReplay = (event) =>
  !(event?.tags?.api_path === '/rest/login' && event?.tags?.api_status === 'network') && !event?.tags?.storage_state;

export const initSentry = () => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: ENVIRONMENT,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // Replaces the deprecated `sendDefaultPii: false` (gone in v11). Defaults here are
    // permissive, and dataCollection wins over sendDefaultPii, so set it explicitly.
    dataCollection: {
      // Drops automatic user fields including ip_address. Explicit Sentry.setUser()
      // still applies, so the email stays searchable — see createIdentitySync.
      userInfo: false,
      // Never capture request/response bodies: the login POST carries credentials and
      // the domain switch carries the JWT.
      httpBodies: []
    },
    tracesSampleRate: 0,
    // Buffer mode: record into a ~60s ring buffer and upload only when an error fires,
    // which yields the lead-up to every error without recording healthy sessions.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        unmask: UNMASK_SELECTORS,
        beforeErrorSampling: shouldSampleErrorForReplay
      })
    ],
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = scrubUrl(event.request.url);
      }

      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'navigation' && breadcrumb.data) {
        breadcrumb.data.from = scrubUrl(breadcrumb.data.from);
        breadcrumb.data.to = scrubUrl(breadcrumb.data.to);
      }

      return breadcrumb;
    }
  });

  userStore.subscribe(createIdentitySync());
};
